import React, { useEffect, useState, useCallback } from "react";
import { StyleSheet, View, ScrollView, RefreshControl } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { router } from "expo-router";
import ThemedScreen from "@/components/ThemedScreen";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { ThemedButton } from "@/components/ThemedButton";
import { ThemedProgressBar } from "@/components/ThemedProgressBar";
import { useFirebase } from "@/components/firebase/useFirebase";
import { useAuth } from "@/components/auth/useAuth";
import { useThemeColor } from "@/hooks/useThemeColor";
import { getUserStorageQuota } from "@/services/firebase/media-functions";
import { IUserStorageQuotaSummary, DEFAULT_STORAGE_QUOTA_BYTES } from "common247";

/**
 * Formats byte values into human-readable strings (e.g. 450 MB, 1.8 GB).
 * @param bytes - The byte count.
 * @returns Human-readable string.
 */
function formatStorage(bytes?: number | null): string {
  if (bytes == null || isNaN(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

/**
 * QuotaScreen displays the user's storage allowance, real-time consumption,
 * and media distribution statistics.
 * @returns {React.ReactElement} The Quota screen component.
 */
export default function QuotaScreen() {
  const services = useFirebase();
  const { user } = useAuth(true);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [quotaData, setQuotaData] = useState<IUserStorageQuotaSummary | null>(null);

  const cardBgColor = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const tintColor = useThemeColor({}, "tint");

  const loadQuota = useCallback(async () => {
    if (!services || !user) return;
    try {
      const res = await getUserStorageQuota(services, {});
      if (res.status && res.quota) {
        setQuotaData(res.quota);
      }
    } catch (err) {
      console.error("Error loading storage quota:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [services, user]);

  useEffect(() => {
    loadQuota();
  }, [loadQuota]);

  const onRefresh = () => {
    setRefreshing(true);
    loadQuota();
  };

  const totalQuota = quotaData?.storageQuotaBytes ?? (quotaData as any)?.quotaBytes ?? DEFAULT_STORAGE_QUOTA_BYTES;
  const used = quotaData?.usedBytes ?? 0;
  const remaining = quotaData?.remainingBytes ?? Math.max(0, totalQuota - used);
  const rawPct = quotaData?.usagePercentage ?? (quotaData as any)?.percentUsed ?? (totalQuota > 0 ? (used / totalQuota) * 100 : 0);
  const percentage = typeof rawPct === "number" && !isNaN(rawPct) ? Math.min(100, Math.max(0, rawPct)) : 0;
  const mediaCount = quotaData?.mediaCount ?? 0;

  // Determine progress bar color based on percentage used
  let barColor = "#10B981"; // Emerald green
  if (percentage >= 90) {
    barColor = "#EF4444"; // Danger red
  } else if (percentage >= 75) {
    barColor = "#F59E0B"; // Amber yellow
  }

  return (
    <ThemedScreen
      title="Storage Quota"
      rightIcon={{
        name: "refresh-outline",
        onPress: onRefresh,
      }}
    >
      <ThemedProgressBar loading={loading} />

      <ScrollView
        contentContainerStyle={styles.scrollContainer}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      >
        {/* Main Quota Gauge Card */}
        <ThemedView style={[styles.card, { backgroundColor: cardBgColor, borderColor }]}>
          <View style={styles.headerRow}>
            <View style={[styles.iconContainer, { backgroundColor: `${tintColor}15` }]}>
              <Ionicons name="cloud-outline" size={24} color={tintColor} />
            </View>
            <View style={styles.headerText}>
              <ThemedText type="subtitle">Cloud Storage</ThemedText>
              <ThemedText style={styles.subtext}>Personal storage allocation</ThemedText>
            </View>
          </View>

          {/* Usage Numbers */}
          <View style={styles.statRow}>
            <View>
              <ThemedText style={styles.largeValue}>{formatStorage(used)}</ThemedText>
              <ThemedText style={styles.label}>Used</ThemedText>
            </View>
            <View style={styles.alignRight}>
              <ThemedText style={styles.largeValue}>{formatStorage(totalQuota)}</ThemedText>
              <ThemedText style={styles.label}>Total Limit</ThemedText>
            </View>
          </View>

          {/* Progress Bar Container */}
          <View style={styles.progressTrack}>
            <View
              style={[
                styles.progressBar,
                {
                  width: `${Math.max(2, Math.min(100, percentage))}%`,
                  backgroundColor: barColor,
                },
              ]}
            />
          </View>

          <View style={styles.progressLabelsRow}>
            <ThemedText style={[styles.percentageText, { color: barColor }]}>
              {percentage.toFixed(1)}% full
            </ThemedText>
            <ThemedText style={styles.remainingText}>
              {formatStorage(remaining)} remaining
            </ThemedText>
          </View>
        </ThemedView>

        {/* Detailed Breakdown Card */}
        <ThemedView style={[styles.card, { backgroundColor: cardBgColor, borderColor }]}>
          <View style={styles.cardHeader}>
            <Ionicons name="pie-chart-outline" size={20} color={tintColor} />
            <ThemedText type="subtitle" style={styles.cardTitle}>Storage Overview</ThemedText>
          </View>

          <View style={styles.detailRow}>
            <View style={styles.detailLeft}>
              <Ionicons name="images-outline" size={18} color="#6B7280" />
              <ThemedText style={styles.detailLabel}>Active Media Files</ThemedText>
            </View>
            <ThemedText style={styles.detailValue}>{mediaCount}</ThemedText>
          </View>

          <View style={[styles.divider, { backgroundColor: borderColor }]} />

          <View style={styles.detailRow}>
            <View style={styles.detailLeft}>
              <Ionicons name="shield-checkmark-outline" size={18} color="#6B7280" />
              <ThemedText style={styles.detailLabel}>Safety Scanned</ThemedText>
            </View>
            <ThemedText style={[styles.detailValue, { color: "#10B981" }]}>100%</ThemedText>
          </View>

          <View style={[styles.divider, { backgroundColor: borderColor }]} />

          <View style={styles.detailRow}>
            <View style={styles.detailLeft}>
              <Ionicons name="server-outline" size={18} color="#6B7280" />
              <ThemedText style={styles.detailLabel}>Storage Quota Policy</ThemedText>
            </View>
            <ThemedText style={styles.detailValue}>Enforced on Upload</ThemedText>
          </View>
        </ThemedView>

        {/* Assistance Card */}
        <ThemedView style={[styles.infoBox, { backgroundColor: `${tintColor}10`, borderColor: `${tintColor}30` }]}>
          <Ionicons name="information-circle-outline" size={20} color={tintColor} style={styles.infoIcon} />
          <View style={styles.infoContent}>
            <ThemedText style={styles.infoTitle}>Need additional storage?</ThemedText>
            <ThemedText style={styles.infoText}>
              Storage limits default to 2 GB per user account. If your account requires additional quota for high-resolution memories or video collections, please contact your workspace administrator.
            </ThemedText>
          </View>
        </ThemedView>

        {/* Action Buttons */}
        <View style={styles.actionButtons}>
          <ThemedButton
            title="Refresh Storage Status"
            variant="secondary"
            onPress={onRefresh}
          />
          <ThemedButton
            title="Back to Profile"
            variant="minimal"
            onPress={() => router.canGoBack() ? router.back() : router.replace("/(tabs)/profile" as any)}
          />
        </View>
      </ScrollView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  scrollContainer: {
    padding: 16,
    gap: 16,
  },
  card: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 20,
    gap: 12,
  },
  iconContainer: {
    width: 44,
    height: 44,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  headerText: {
    flex: 1,
  },
  subtext: {
    fontSize: 13,
    color: "#6B7280",
    marginTop: 2,
  },
  statRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginBottom: 14,
  },
  alignRight: {
    alignItems: "flex-end",
  },
  largeValue: {
    fontSize: 24,
    fontWeight: "700",
  },
  label: {
    fontSize: 12,
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginTop: 2,
  },
  progressTrack: {
    height: 12,
    borderRadius: 6,
    backgroundColor: "#E5E7EB",
    overflow: "hidden",
    marginBottom: 8,
  },
  progressBar: {
    height: "100%",
    borderRadius: 6,
  },
  progressLabelsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  percentageText: {
    fontSize: 13,
    fontWeight: "700",
  },
  remainingText: {
    fontSize: 13,
    color: "#6B7280",
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 16,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: "600",
  },
  detailRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 10,
  },
  detailLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  detailLabel: {
    fontSize: 14,
    color: "#4B5563",
  },
  detailValue: {
    fontSize: 14,
    fontWeight: "600",
  },
  divider: {
    height: 1,
    marginVertical: 2,
  },
  infoBox: {
    flexDirection: "row",
    borderRadius: 12,
    borderWidth: 1,
    padding: 14,
    gap: 10,
  },
  infoIcon: {
    marginTop: 2,
  },
  infoContent: {
    flex: 1,
  },
  infoTitle: {
    fontSize: 14,
    fontWeight: "600",
    marginBottom: 4,
  },
  infoText: {
    fontSize: 13,
    color: "#4B5563",
    lineHeight: 18,
  },
  actionButtons: {
    marginTop: 8,
    gap: 10,
  },
});
